// Month grid + day cell anatomy (DESIGN §3).
//
// Cell layout, top to bottom — this ordering is the spec, not a preference:
//
//   ┌─────────┐
//   │  12  🌸 │  date number (+ cycle badge, top-right of the number)
//   │ ⚑ ⚑    │  event chips — **three slots, all of them events** (W58.5)
//   │ ⚑  +2   │  third slot + overflow
//   └─────────┘
//   ▒▒▒▒▒▒▒▒   ← the cell's own background = the cycle state (W58.2)
//
// **W58.5 —— 三個槽位 100% 是事件。** 以前週期 marker 佔走 row A 的第一格
// （而後端又已經把 `category="cycle"` 從 chip 清單裡排除），於是**有經期的
// 那天只畫得下 2 個事件**。現在週期是一朵**角標**（數字的右上角）加一層
// **底色**，格子半部整個交給雙人事件與其他模組的事件。
//
// Two rules that are easy to break and that the tests hold:
//
// **The cycle state is the cell's own background** (W58.2 / W58.3). It used
// to be a separate 3px bar pinned to the cell's bottom — a "cycle Range
// Pill" — which was folded into nothing and could never be read as "these
// are the days of a period". A tinted cell says it in one glance, and the
// pill's continuity claim could not be honoured anyway: bridging a 4px
// gutter across 9px-rounded cells leaves a notch at every seam.
//
// **No cell background tint for shared time** (DESIGN §5.2). A tinted cell is
// the loudest signal a month grid has, it competes with today / selected /
// has-events / out-of-month, and it is ambiguous between "we're both free" and
// "something is booked". Shared time lives in the panel above the grid and in
// the day sheet's prose, never here.
//
// **W58.1 例外**：實際記錄的經期**有**底色（`bg-partner/25`）。
// 這是使用者明確要求的（「柔和粉紅/珊瑚紅背景色塊」），而且它跟共同時間
// 不同 —— 共同時間的底色會與「有事件 / 沒事件」混淆，而經期底色說的是
// 「這一天的身體狀態」，跟事件無關。25% 的玫瑰在米白紙面上是很輕的
// 一層，不會跟「今天」的墨色環或「選中」的藍框搶視覺。
//
// **The day cell is the only click target in a cell** (W40.1 F4). It used to
// hold three pseudo-buttons inside a real `<button>` — the chip, the `+N`
// overflow, and the inference band — each with its own `onClick` +
// `stopPropagation`, so "what does tapping this do?" had four answers on one
// 48×44 box and the chip's answer depended on a 15×15 target. All three are now
// pure presentation. A single event is reached from the day sheet's own
// 「查看 / 編輯」 rows, and the cycle record from its 「查看週期紀錄」 — so
// nothing became unreachable, and the cell's `aria-label` ("N 項安排") is the
// only density cue a cell needs.

import { cn } from "@/lib/utils/cn";
import { CATEGORY_ICON, chipClasses } from "@/components/calendar/calendarIcons";
import { CycleBadge } from "@/components/calendar/CycleCellVisuals";
import { cycleVisualKind, cycleWash, resolveCycleState } from "@/components/calendar/cycleState";
import type { CalendarDayCell, CalendarItem, FlowValue } from "@/lib/api/calendar";
import { FLOW_LABEL } from "@/lib/api/calendar";

const WEEKDAY_SHORT = ["一", "二", "三", "四", "五", "六", "日"] as const;

/**
 * W58.0 — 螢幕閱讀器要聽得到 rail 講什麼。
 *
 * rail 本身是 `aria-hidden`（它是一道 3px 的裝飾），所以階段資訊只能走
 * 格子自己的 `aria-label`。**「（推算）」不能省** —— 螢幕閱讀器看不到
 * 玫瑰色與墨色的分別，但「這是推算」這件事是 DATA_MODEL §5 的核心。
 */
const PHASE_LABEL: Record<string, string> = {
  menstrual: "經期",
  follicular: "卵泡期",
  fertile: "易孕期",
  ovulation: "排卵期",
  luteal: "黃體期",
};

export function WeekdayHeader() {
  return (
    <div
      className="grid grid-cols-7 gap-s-1 px-s-1 pb-s-2"
      aria-hidden="true"
      data-testid="calendar-weekday-header"
    >
      {WEEKDAY_SHORT.map((d) => (
        <div key={d} className="text-center text-micro text-ink-3">
          {d}
        </div>
      ))}
    </div>
  );
}

export interface MonthGridProps {
  weeks: CalendarDayCell[][];
  selectedDate: string | null;
  onSelectDay: (date: string) => void;
  /** Long-press opens the create sheet pre-filled with that day (DESIGN §7). */
  onLongPressDay: (date: string) => void;
}

export function MonthGrid({
  weeks,
  selectedDate,
  onSelectDay,
  onLongPressDay,
}: MonthGridProps) {
  return (
    <div className="flex flex-col gap-s-1" data-testid="calendar-grid">
      {weeks.map((week, wi) => (
        <div key={wi} className="grid grid-cols-7 gap-s-1">
          {week.map((cell) => (
            <DayCell
              key={cell.date}
              cell={cell}
              selected={cell.date === selectedDate}
              onSelect={onSelectDay}
              onLongPress={onLongPressDay}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

interface DayCellProps {
  cell: CalendarDayCell;
  selected: boolean;
  onSelect: (date: string) => void;
  onLongPress: (date: string) => void;
}

const LONG_PRESS_MS = 500;

function DayCell({ cell, selected, onSelect, onLongPress }: DayCellProps) {
  const day = Number(cell.date.slice(8, 10));
  const cycleState = resolveCycleState(cell);
  // W58.3 — **底色、花、圖例由同一個狀態決定**（`cycleVisualKind`）。
  // 以前底色看 `cycleWash()`、花看另一個 `markerKind()`，兩份定義各自
  // 漂移，於是圖例寫「排卵」而格子其實是「排卵日」。
  const visualKind = cycleVisualKind(cycleState);
  // W58.3 — 底色查一次就好：它同時決定「這格染什麼色」與「要不要掛 hover 灰底」。
  const wash = cycleWash(cycleState);

  // Long-press without a gesture library: a timer armed on pointerdown and
  // cancelled on pointerup / pointerleave / scroll. 500ms is inside the HIG
  // long-press window (150–250ms is Apple's quick-action range; the calendar
  // mockup and DESIGN §7 settle on a longer press so a plain tap is never at
  // risk of being read as a long press).
  let timer: ReturnType<typeof setTimeout> | undefined;
  const armLongPress = () => {
    timer = setTimeout(() => onLongPress(cell.date), LONG_PRESS_MS);
  };
  const cancelLongPress = () => {
    if (timer) clearTimeout(timer);
    timer = undefined;
  };

  /**
   * 事件 chip 清單。**經期不在裡面** —— 後端在 `build_day_cells` 就把它
   * 排除了（月曆格上由週期 marker 代表），所以「chip 上限」的帳由後端算，
   * 前端不會數錯。
   */
  const eventItems = cell.items;

  // 實際記錄的經期是一種安排，即使它不再是一個 chip。
  const arrangementCount = cell.items.length + (cycleState?.actual ? 1 : 0);

  // W58.5 — **格子只有三個槽位，但上限可以設到 10。**
  //
  // `cell.items` 是後端按 `max_per_day` 截斷之後的清單，而 `overflow_count`
  // 是**超出那個上限**的數量。當上限是 5 或 10 的時候，清單裡有 5 或 10 筆，
  // 而格子畫得下 3 筆 —— 剩下筆**沒有出現在任何地方**（`overflow_count`
  // 是 0，因為它們沒有超出上限，是超出**格子**）。
  //
  // 所以溢位要自己算一次：`+N` 必須是「這一天看不到的安排總數」，不是
  // 「超出後端上限的數量」。少了這一段，設了 5 或 10 的使用者會安靜地
  // 少看見兩筆到七筆安排。
  const overflowCount = cell.overflow_count + Math.max(0, cell.items.length - 3);

  const cycleLabel = cycleState?.phase
    ? PHASE_LABEL[cycleState.phase] ?? cycleState.phase
    : null;

  return (
    <button
      type="button"
      // R0 (W39.0): the day cell was `<div role="button" tabIndex={0}>` with a
      // real `<button>` chip nested inside it. A `<button>` inside a
      // `role="button"` is announced as "button inside a button" and, before
      // this fix, the inner chip's click also bubbled into the cell's own
      // onSelect. Making the cell the *real* button and the chips plain
      // `role="button"` spans flattens the tree: the semantics read as three
      // sibling controls instead of a button-inside-a-button, and the native
      // element brings Enter/Space activation for free (the manual onKeyDown
      // that emulated it is gone — a native button does not double-fire it).
      // 44×44 minimum hit target (Apple HIG, DESIGN §15). **This cell does not
      // meet it in width, deliberately, and the previous version of this comment
      // claimed otherwise** — it said the tap area was "padded out", and there
      // has never been a padding-out on this element (W46.0).
      //
      // The grid is 7 columns. At 360px the usable width is ~328px, so after the
      // `gap-s-1` gutters each cell is **43.4 × 69**, and at 390px it is ~40 × 69.
      // Reaching 44 needs 7 × 44 + 6 × 4 = 332px of content, which does not fit
      // inside 328px. Widening the *target* without widening the *cell* does not
      // work either: the cells' centres sit 43.7px apart, so a 44px-wide
      // `::after` overlay would overlap its neighbour and a tap aimed at Tuesday
      // would land on Monday.
      //
      // So this is the one exemption DESIGN.md §15 actually grants — a target whose
      // neighbours are close enough that widening it creates overlap. The
      // alternatives are all worse: shrinking the gutter breaks the app's 16px
      // rhythm, and dropping to 6 columns changes what the calendar *is*.
      //
      // So the honest position is: this control is 43.4 × 69, under the floor in
      // one axis by 0.6px, on the narrowest supported phone, and the sweep in
      // `scripts/mobile-layout-audit.mjs` carries it as a named exception rather
      // than pretending otherwise or "fixing" it into mis-taps.
      className={cn(
        // `calendar-cell` carries the box (W45.0: `min-height` and radius from
        // `--cell-h` / `--cell-r`, the same token the embedded mini-grid reads).
        // The 44px floor in `AGENTS.md §15` is now carried by the token rather
        // than by a literal, which is why the `min-h-[44px]` utility is gone —
        // keeping both would be two sources for one number.
        "calendar-cell relative flex w-full flex-col items-center",
        // W46.0: `press`. This cell is the calendar's primary tap target and the
        // only thing confirming *which* day a thumb landed on — `transition-colors`
        // was already here, but nothing changed under `:active`, so the sweep
        // measured 30 cells that look identical pressed and released.
        //
        // W48.0: the border is **2px and transparent by default**, because both
        // day states are frames now and a frame that appears by growing the box
        // moves every cell to its right by 1px — a month grid that shifts a
        // column when you tap one day. The two states are painted from
        // `data-selected` / `data-today` in `globals.css`, in two different
        // channels (border vs inset ring) so a day that is both keeps both.
        "press border-2 border-transparent py-1.5 pb-2 transition-colors duration-quick",
        cell.in_month ? "bg-transparent" : "opacity-40",
        // W58.2 — 每種週期狀態都有底色；底色是狀態的主要視覺通道。
        wash,
        // W58.3 — **有底色的格子不再掛 hover 的灰底。**
        //
        // `hover:bg-black/5` 與 `bg-partner/25` 是兩個 background-color
        // 工具類，而 hover 變體在執行期的特異性更高 —— 所以一 hover，
        // 週期底色就被灰底蓋掉。底色升格成唯一通道之後，這等於「指到
        // 那一格時它就不像經期了」。讓週期格保留自己的底色。
        wash ? null : "hover:bg-black/5",
      )}
      data-testid={`calendar-day-${cell.date}`}
      data-in-month={cell.in_month}
      data-today={cell.is_today || undefined}
      data-selected={selected || undefined}
      data-inference={cell.inference || undefined}
      data-cycle-state={cycleState?.phase ?? undefined}
      aria-label={`${cell.date}${cell.is_today ? " 今天" : ""}${
        cycleLabel ? `，${cycleLabel}` : ""
      }${
        // **「推算」這件事必須在讀音裡。** 底色與花都是視覺通道，
        // 螢幕閱讀器看不到玫瑰與墨色的分別 —— 所以「這是推算」只能靠
        // 這句話（DATA_MODEL §5 的核心）。
        cycleState?.actual
          ? "（已記錄）"
          : cycleLabel || cycleState?.predicted
            ? "（推算）"
            : ""
      }${
        // W58.1 — 流量也進讀音。marker 是一朵花，看不出那天是「點滴」還是「多」，
        // 而那正是使用者逐日記下來的東西 —— 螢幕閱讀器不該是唯一看不到它的人。
        cycleState?.flow
          ? `，流量${FLOW_LABEL[cycleState.flow as FlowValue] ?? cycleState.flow}`
          : ""
      }${
        arrangementCount > 0 ? `，${arrangementCount} 項安排` : "，沒有安排"
      }`}
      onClick={() => onSelect(cell.date)}
      onPointerDown={armLongPress}
      onPointerUp={cancelLongPress}
      onPointerLeave={cancelLongPress}
      onPointerCancel={cancelLongPress}
    >
      {/*
        W58.5 — **日期數字 + 右上角的微型花角標。**

        角標是**絕對定位在「格子」上**（不是數字上），**10px**，
        `right-0 top-0` —— 也就是格子內側的右上角。

        **為什麼不錨在數字上。** W58.1 實測過：角標放在**格子**的右上角時，
        320px 的格子上它撞到日期數字 5.4px。錨在數字上雖然避開了那一撞，
        卻讓角標**溢出格子 2.4px**（實測）—— 320px 的格子只有 37.7px 寬，
        而置中的兩位數字佔 18.6px，右邊只剩 7.6px，塞不下一朵 10px 的花。

        **現在的答案是：讓角標佔掉那塊「上padding」，而不是往右擠。**
        格子的內容從 `border(2) + pt(6)` = 8px 處開始，而角標佔 2..12px ——
        它落在**數字上方**（數字的墨在 13.5px 之後），所以**不碰數字**，
        也不溢出格子。
      */}
      <span
        className={cn(
          // W48.0: today is no longer a **filled disc** on the number — it is an
          // ink ring around the whole cell, painted in `globals.css` from
          // `data-today`, so it survives being selected at the same time instead
          // of being swallowed by the selection frame. The number itself is
          // plain `ink-1` for every in-month day, today included.
          "text-meta tabular-nums leading-5",
          cell.in_month ? "text-ink-1" : "text-ink-3",
        )}
      >
        {day}
      </span>

      {/* W58.5 — **角標取代了 chip 行裡的 marker。** 事件槽位 100% 留給事件。

          `-top-1`（-4px）讓角標**探出格子頂端 2px**，落在格間距裡 ——
          於是它整個都在**數字的行盒之上**（數字的墨在行盒下方 4px），
          既不壓到數字，也不溢出格子的左右。 */}
      <CycleBadge kind={visualKind} className="pointer-events-none absolute right-0 -top-1" />

      {/*
        W45.0 `ADR-0111` §P7 — **two fixed rows**, per the F21/F24 spec that
        `mockup/w42.0-calendar.html` states and the build never implemented:
        row A takes 2 chips, row B takes 1 chip plus the overflow count.

        The single row it replaces had no width budget at all, so a busy day ran
        its chips past the cell — the three icons and a `+1` spilling out of a
        7-column cell in the device screenshot.

        **Both rows always render.** A day with one event leaves row B empty
        rather than collapsing the cell, because cells of differing heights make
        the month read as a wave instead of a grid. That is why the row carries
        `min-height` (in `.calendar-cell__row`, `globals.css §11d`) rather
        than its content deciding.

        **W58.5 —— 三個槽位全部是事件。** 以前週期 marker 佔走 row A 的第一格
        （後端又已經把 `category="cycle"` 從 chip 清單裡排除），所以那一天
        只畫得下 **2 個事件**。現在週期改成一朵角標，**3 個槽位全部是事件** ——
        與後端 `max_per_day` 的預設值 3 一致。
      */}
      <div className="calendar-cell__rows">
        <div className="calendar-cell__row">
          {eventItems.slice(0, 2).map((item, i) => (
            <Chip key={`a-${item.category}-${item.owner}-${i}`} item={item} />
          ))}
        </div>
        <div className="calendar-cell__row">
          {eventItems.slice(2, 3).map((item, i) => (
            <Chip key={`b-${item.category}-${item.owner}-${i}`} item={item} />
          ))}
          {overflowCount > 0 ? (
            // Presentation only (W40.1 F4). It used to be a `role="button"` span
            // that opened the same day sheet the cell itself opens, carrying an
            // `after:` overlay to claim 44pt of a 15px-tall row. With the cell
            // being the one target, an 11px number with its own focus stop and
            // its own key handler was a second door to a room with one door.
            <span
              className="text-micro font-semibold text-ink-2"
              data-testid={`calendar-overflow-${cell.date}`}
            >
              +{overflowCount}
            </span>
          ) : null}
        </div>
      </div>
    </button>
  );
}

/**
 * A 15×15 category/owner swatch (DESIGN §3). Presentation only since W40.1 F4:
 * `aria-hidden`, no `role`, no `tabIndex`, no `after:` hit-area overlay. The
 * chip used to open a single event straight from the grid — the one path F4
 * removed — and the day sheet's per-item 「查看 / 編輯」 row is now the only
 * one, keeping its own 44pt size and `aria-label`.
 *
 * The cost is recorded rather than hidden: the 44pt claim this chip carried
 * goes with the chip, and the day cell (44px tall) is the sole target for that
 * whole area.
 */
function Chip({ item }: { item: CalendarItem }) {
  const Icon = CATEGORY_ICON[item.category];
  return (
    <span
      aria-hidden
      className={cn(
        // W48.0: 18px box / 13px glyph, from `--chip-h` (was 15 / 11). Full-width
        // days buy the room: at 430px a cell is 53.4px, so two chips plus the
        // 3px gutter come to 39px with room over — at the old capped 390px the
        // cell was ~40px and 15px chips left the icons unreadably small.
        //
        // The size reads the **token** rather than a literal so the box and the
        // row's `min-height` cannot become two numbers for one chip.
        "flex h-[var(--chip-h)] w-[var(--chip-h)] items-center justify-center rounded-[6px]",
        "transition-opacity duration-quick",
        chipClasses(item.owner),
      )}
      data-testid={`calendar-chip-${item.category}-${item.owner}`}
    >
      <Icon size={13} strokeWidth={2} aria-hidden="true" />
    </span>
  );
}
